import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, InputBase, List, ListItemButton, ListItemIcon, ListItemText,
  Stack, Typography, Chip, Box, Divider,
} from '@mui/material';
import {
  Search as SearchIcon, HomeOutlined, RocketLaunchOutlined, ShareOutlined,
  BugReportOutlined, AppsOutlined, ListAltOutlined, AccessTimeOutlined, ScheduleOutlined,
  CampaignOutlined, HelpOutlineRounded, NotificationsActiveOutlined, Apple, Android,
  ArrowForwardRounded, LogoutOutlined, AutorenewRounded, KeyboardReturn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAppDetailsDrawer } from './AppDetailsDrawerContext';

type CommandKind = 'page' | 'app' | 'action';

interface Command {
  id: string;
  kind: CommandKind;
  label: string;
  hint?: string;
  keywords?: string[];
  icon: React.ReactElement;
  onRun: () => void;
}

const PAGES: Array<{ id: string; label: string; path: string; icon: React.ReactElement; hint: string }> = [
  { id: 'page:home', label: 'Home', path: '/home', icon: <HomeOutlined fontSize="small" />, hint: 'Greeting, watch list, activity feed' },
  { id: 'page:deployment', label: 'App Deployment', path: '/home/deployment', icon: <RocketLaunchOutlined fontSize="small" />, hint: 'Trigger builds' },
  { id: 'page:scheduler', label: 'Deploy Scheduler', path: '/home/deploy-scheduler', icon: <ScheduleOutlined fontSize="small" />, hint: 'Queue deploys for later' },
  { id: 'page:distribution', label: 'App Distribution', path: '/home/app-distribution', icon: <ShareOutlined fontSize="small" />, hint: 'Fleet charts' },
  { id: 'page:apps', label: 'Config Readiness', path: '/home/apps', icon: <AppsOutlined fontSize="small" />, hint: 'Apps missing site key / bundle id / deployment' },
  { id: 'page:builds', label: 'Build Browser', path: '/home/builds', icon: <ListAltOutlined fontSize="small" />, hint: 'Cross-app pipeline list' },
  { id: 'page:analytics', label: 'Build Analytics', path: '/home/build-analytics', icon: <BugReportOutlined fontSize="small" />, hint: 'Failure dashboards' },
  { id: 'page:cost', label: 'Build Duration', path: '/home/build-cost', icon: <AccessTimeOutlined fontSize="small" />, hint: 'Build durations per app & per day' },
  { id: 'page:errors', label: 'Error Logs', path: '/home/error-logs', icon: <BugReportOutlined fontSize="small" />, hint: 'Searchable failure inspector' },
  { id: 'page:audit', label: 'Audit Log', path: '/home/audit', icon: <ListAltOutlined fontSize="small" />, hint: 'Operator action stream' },
  { id: 'page:notes', label: 'Release Notes', path: '/home/release-notes', icon: <CampaignOutlined fontSize="small" />, hint: 'App Manager release notes' },
  { id: 'page:help', label: 'Help & Tutorial', path: '/home/tutorial', icon: <HelpOutlineRounded fontSize="small" />, hint: 'Walkthrough of every page' },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Global ⌘K / Ctrl+K command palette. Sources:
 *   • Top-nav pages (always present)
 *   • Apps from useAuth().appData — composer host + app name searchable
 *   • Quick actions — refresh table, toggle notifications, logout
 * Results are scored simply: prefix match > substring match > keyword match.
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { openAppDetails } = useAppDetailsDrawer();
  const { appData } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Reset state when dialog opens/closes so each invocation feels fresh.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
    }
  }, [open]);

  const allCommands = useMemo<Command[]>(() => {
    const pages: Command[] = PAGES.map((p) => ({
      id: p.id,
      kind: 'page',
      label: p.label,
      hint: p.hint,
      keywords: [p.label.toLowerCase(), p.path],
      icon: p.icon,
      onRun: () => { navigate(p.path); onClose(); },
    }));

    const apps: Command[] = appData
      ? Object.entries(appData as Record<string, any>).map(([key, app]) => {
          const host = app.domain || app.composerHost || key;
          const name = app.app_name || app.appName || host;
          const platform = (app.platform || '').toLowerCase();
          return {
            id: `app:${key}`,
            kind: 'app' as const,
            label: name,
            hint: host,
            keywords: [name.toLowerCase(), String(host).toLowerCase(), platform],
            icon: platform === 'ios'
              ? <Apple fontSize="small" sx={{ color: '#0f172a' }} />
              : <Android fontSize="small" sx={{ color: '#3ddc84' }} />,
            onRun: () => {
              // Open the details slider over the current page. Pass stable
              // identifiers so dual-platform / duplicate-host apps resolve to
              // this exact entry.
              openAppDetails(String(host), platform, app.composer_app_id ?? app.composerAppId);
              onClose();
            },
          };
        })
      : [];

    const actions: Command[] = [
      {
        id: 'action:refresh',
        kind: 'action',
        label: 'Refresh dashboard data',
        hint: 'Re-fetch the recent-builds feed',
        keywords: ['refresh', 'reload', 'fetch', 'sync'],
        icon: <AutorenewRounded fontSize="small" />,
        onRun: () => {
          window.dispatchEvent(new CustomEvent('fsam:refresh-dashboard'));
          onClose();
        },
      },
      {
        id: 'action:notifications',
        kind: 'action',
        label: 'Toggle build notifications',
        hint: 'Opens App Deployment to manage the bell',
        keywords: ['notifications', 'bell', 'alerts'],
        icon: <NotificationsActiveOutlined fontSize="small" />,
        onRun: () => { navigate('/home/deployment'); onClose(); },
      },
      {
        id: 'action:replay-onboarding',
        kind: 'action',
        label: 'Replay onboarding tour',
        hint: 'Re-run the first-login walkthrough',
        keywords: ['onboarding', 'tour', 'tutorial', 'walkthrough', 'getting started', 'intro', 'help'],
        icon: <HelpOutlineRounded fontSize="small" />,
        onRun: () => {
          window.dispatchEvent(new CustomEvent('fsam:open-onboarding'));
          onClose();
        },
      },
      {
        id: 'action:logout',
        kind: 'action',
        label: 'Log out',
        hint: 'End the current session',
        keywords: ['logout', 'sign out', 'signout', 'exit'],
        icon: <LogoutOutlined fontSize="small" />,
        onRun: () => {
          try { localStorage.removeItem('token'); } catch { /* ignore */ }
          window.location.href = '/login';
        },
      },
    ];

    return [...pages, ...apps, ...actions];
  }, [appData, navigate, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Show pages + first 12 apps as the default empty-state list — keeps the
      // palette useful before the user has typed anything.
      return [
        ...allCommands.filter((c) => c.kind === 'page'),
        ...allCommands.filter((c) => c.kind === 'action'),
        ...allCommands.filter((c) => c.kind === 'app').slice(0, 12),
      ];
    }
    const score = (c: Command) => {
      const label = c.label.toLowerCase();
      const hint = (c.hint || '').toLowerCase();
      const kws = (c.keywords || []).join(' ');
      if (label.startsWith(q)) return 100;
      if (label.includes(q)) return 70;
      if (hint.includes(q)) return 50;
      if (kws.includes(q)) return 30;
      return 0;
    };
    return allCommands
      .map((c) => ({ c, s: score(c) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 30)
      .map((x) => x.c);
  }, [allCommands, query]);

  // Keep the active index inside the result list as it shrinks.
  useEffect(() => { setActiveIdx(0); }, [query]);

  const runActive = () => {
    const cmd = results[activeIdx];
    if (cmd) cmd.onRun();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runActive();
    }
  };

  // Scroll the active row into view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row-idx="${activeIdx}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', mt: '8vh', alignSelf: 'flex-start' } }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <SearchIcon sx={{ color: '#64748b' }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Jump to a page, app, or action…"
          sx={{ fontSize: '1rem', flex: 1 }}
        />
        <Chip
          size="small"
          label="esc"
          onClick={onClose}
          sx={{ fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer', bgcolor: '#f1f5f9' }}
        />
      </Stack>

      <DialogContent ref={listRef as any} sx={{ p: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {results.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No matches for <strong>{query}</strong>.</Typography>
            <Typography variant="caption">Try a page name, app host, or "logout".</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {results.map((cmd, i) => {
              const active = i === activeIdx;
              const groupLabel = cmd.kind === 'page' ? 'Page' : cmd.kind === 'app' ? 'App' : 'Action';
              const groupColor = cmd.kind === 'page' ? '#4338ca' : cmd.kind === 'app' ? '#0ea5e9' : '#16a34a';
              const groupBg = cmd.kind === 'page' ? 'rgba(99, 102, 241, 0.1)' : cmd.kind === 'app' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(22, 163, 74, 0.1)';
              return (
                <ListItemButton
                  key={cmd.id}
                  data-row-idx={i}
                  selected={active}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => cmd.onRun()}
                  sx={{
                    py: 1, px: 2,
                    '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.08)' },
                    '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.12)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{cmd.icon}</ListItemIcon>
                  <ListItemText
                    primary={cmd.label}
                    secondary={cmd.hint}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                  />
                  <Chip size="small" label={groupLabel} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: groupBg, color: groupColor, mr: active ? 1 : 0 }} />
                  {active && <ArrowForwardRounded sx={{ fontSize: 16, color: 'text.secondary' }} />}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Divider />
      <Stack direction="row" spacing={2} sx={{ px: 2, py: 1, bgcolor: '#f8fafc' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <KeyShortcut label="↑↓" /> <Typography variant="caption" color="text.secondary">navigate</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <KeyShortcut icon={<KeyboardReturn fontSize="inherit" />} /> <Typography variant="caption" color="text.secondary">open</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <KeyShortcut label="esc" /> <Typography variant="caption" color="text.secondary">close</Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{results.length} result{results.length === 1 ? '' : 's'}</Typography>
      </Stack>
    </Dialog>
  );
};

const KeyShortcut: React.FC<{ label?: string; icon?: React.ReactElement }> = ({ label, icon }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 22, height: 20, px: 0.75, borderRadius: 1,
      bgcolor: '#fff', border: '1px solid #e2e8f0', color: '#475569',
      fontSize: '0.7rem', fontWeight: 700, fontFamily: 'monospace',
    }}
  >
    {icon || label}
  </Box>
);

/** Hook: registers the global ⌘K / Ctrl+K listener and returns palette state. */
export const useCommandPalette = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isPaletteKey = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isPaletteKey) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return {
    open,
    openPalette: () => setOpen(true),
    closePalette: () => setOpen(false),
  };
};

export default CommandPalette;
